"use client";

import { useState, useEffect, useRef, useId } from "react";
import { useRouter } from "@/lib/utm/hooks";
import api from "@/lib/api";
import { ApiRoutes, AppRoutes } from "@/lib/routeResolver";
import styles from "./package-name-search.module.css";

interface Suggestion {
  id: string;
  packageName: string;
  pageURL: string;
  nights: number;
  photo: string;
  countries: string;
  startingFrom: number;
}

interface PackageNameSearchProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  size?: "lg" | "md";
  placeholder?: string;
}

const MIN_CHARS = 2;
const MAX_SUGGESTIONS = 6;
const DEBOUNCE_MS = 250;

function toSuggestion(pack: Record<string, unknown>): Suggestion {
  const regions =
    (pack.packageRegions as { countryName?: string }[] | null) ?? [];
  const gallery = (pack.photoGallery as { url?: string }[] | null) ?? [];
  const rate = (pack.rate as { startingFrom?: number | string } | null) ?? {};
  const countries = [
    ...new Set(regions.map((r) => r.countryName).filter(Boolean)),
  ];

  return {
    id: pack.id as string,
    packageName: pack.packageName as string,
    pageURL: pack.pageURL as string,
    nights: (pack.nights as number) ?? 0,
    photo: gallery[0]?.url ?? "",
    countries:
      countries.slice(0, 2).join(", ") + (countries.length > 2 ? " +" : ""),
    startingFrom: Number(rate.startingFrom ?? 0),
  };
}

function Highlight({ text, query }: { text: string; query: string }) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0 || !query) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function PackageNameSearch({
  value,
  onChange,
  onSubmit,
  size = "md",
  placeholder = "e.g. Highlights of Istanbul",
}: PackageNameSearchProps) {
  const router = useRouter();
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Suggestion[]>([]);
  // The query the current `results` belong to; differs while a fetch is pending.
  const [resultsFor, setResultsFor] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);

  const query = value.trim();
  const canSuggest = query.length >= MIN_CHARS;
  const loading = canSuggest && resultsFor !== query;

  useEffect(() => {
    if (!canSuggest) return;

    const controller = new AbortController();
    const timer = setTimeout(() => {
      api
        .get(ApiRoutes.INVENTORY_PACKAGE_MINI_WEBSITE, {
          params: {
            SearchString: query,
            IsWebsite: true,
            BrandId: process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS,
            PageNumber: 1,
            PageSize: MAX_SUGGESTIONS,
          },
          signal: controller.signal,
        })
        .then((res) => {
          const raw: Record<string, unknown>[] = res?.data?.data ?? [];
          setResults(raw.filter((p) => p.pageURL).map(toSuggestion));
          setResultsFor(query);
          setActiveIdx(-1);
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setResults([]);
            setResultsFor(query);
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, canSuggest]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const showPanel = open && canSuggest;
  // The trailing "see all" row is navigable too, so it takes the last index.
  const optionCount = results.length + 1;

  const goToPackage = (s: Suggestion) => {
    setOpen(false);
    router.push(AppRoutes.package(s.pageURL));
  };

  const submit = () => {
    setOpen(false);
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && canSuggest) {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i + 1) % optionCount);
    } else if (e.key === "ArrowUp" && canSuggest) {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => (i <= 0 ? optionCount - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (showPanel && activeIdx >= 0 && activeIdx < results.length) {
        goToPackage(results[activeIdx]);
      } else if (query) {
        submit();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <div
      ref={wrapRef}
      className={`${styles.wrap} ${size === "lg" ? styles.lg : ""}`}
    >
      <div className={`${styles.field} ${showPanel ? styles.fieldOpen : ""}`}>
        <i className={`fa-light fa-magnifying-glass ${styles.leadIcon}`} />
        <input
          type="text"
          role="combobox"
          aria-label="Package name"
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-activedescendant={
            showPanel && activeIdx >= 0 ? optionId(activeIdx) : undefined
          }
          autoComplete="off"
          enterKeyHint="search"
          className={styles.input}
          value={value}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {loading ? (
          <i className={`fa-solid fa-spinner fa-spin ${styles.trailIcon}`} />
        ) : (
          value && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => onChange("")}
              aria-label="Clear package name"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )
        )}
      </div>

      {showPanel && (
        <div className={styles.panel}>
          <ul id={listId} role="listbox" className={styles.list}>
            {results.map((s, i) => (
              <li
                key={s.id}
                id={optionId(i)}
                role="option"
                aria-selected={activeIdx === i}
                className={`${styles.option} ${activeIdx === i ? styles.optionActive : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => goToPackage(s)}
              >
                <span className={styles.thumb}>
                  {s.photo && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={s.photo} alt="" loading="lazy" />
                  )}
                </span>
                <span className={styles.optionBody}>
                  <span className={styles.optionName}>
                    <Highlight text={s.packageName} query={query} />
                  </span>
                  <span className={styles.optionMeta}>
                    {s.nights > 0 &&
                      `${s.nights} ${s.nights === 1 ? "night" : "nights"}`}
                    {s.nights > 0 && s.countries && " · "}
                    {s.countries}
                  </span>
                </span>
                {s.startingFrom > 0 && (
                  <span className={styles.optionPrice}>
                    <small>from</small>$
                    {s.startingFrom.toLocaleString("en-US")}
                  </span>
                )}
              </li>
            ))}

            {!loading && results.length === 0 && (
              <li className={styles.empty} aria-hidden>
                No package names match “{query}”. Search anyway to include
                your other filters.
              </li>
            )}

            <li
              id={optionId(results.length)}
              role="option"
              aria-selected={activeIdx === results.length}
              className={`${styles.seeAll} ${activeIdx === results.length ? styles.optionActive : ""}`}
              onMouseEnter={() => setActiveIdx(results.length)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={submit}
            >
              <i className="fa-light fa-magnifying-glass" />
              <span>
                See all results for <strong>“{query}”</strong>
              </span>
              <kbd>Enter</kbd>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
